import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../context/ToastContext';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareSelectField from '../common/SalovareSelectField';
import SwitchField from '../common/SwitchField';
import StoreService from '../../services/storeService';
import { buildAdornmentTextFieldSx, fieldLabelSx } from '../common/formFieldStyles';
import { DEFAULT_CURRENCY, getCurrencySymbol } from '../../utils/currencyUtils';
import {
  getExtraHoursEndOptions,
  getExtraHoursStartOptions,
} from '../../utils/authorizationHoursTimeOptions';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const WEEK_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const ADJUSTMENT_TYPES = ['Percentage', 'FixedAmount'];

const toApiWeekDay = (day) => day.charAt(0).toUpperCase() + day.slice(1);

const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour += 1) {
    for (let minute = 0; minute < 60; minute += 10) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      options.push({ value: timeString, label: timeString });
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

const unitForAdjustment = (adjustmentType, currencySymbol) => (
  adjustmentType === 'Percentage' ? '%' : currencySymbol
);

const createRuleId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createEmptyRule = () => ({
  id: createRuleId(),
  weekDays: [],
  allDay: true,
  periods: [{ from: '19:00', to: '21:00' }],
  adjustmentType: 'Percentage',
  adjustmentValue: '',
});

const fromApiRules = (timePricing) => ({
  shareType: timePricing?.employeeAdditionalCostShareType || 'Percentage',
  shareValue: timePricing?.employeeAdditionalCostShareValue ?? 0,
  rules: (timePricing?.rules || []).map((rule) => ({
    id: createRuleId(),
    weekDays: (rule.weekDays || []).map((day) => String(day).toLowerCase()),
    allDay: Boolean(rule.allDay),
    periods: rule.allDay
      ? [{ from: '19:00', to: '21:00' }]
      : (rule.periods || []).map((period) => ({ from: period.from, to: period.to })),
    adjustmentType: rule.adjustmentType || 'Percentage',
    adjustmentValue: rule.adjustmentValue ?? '',
  })),
});

const toApiPayload = (shareType, shareValue, rules) => ({
  employeeAdditionalCostShareType: shareType,
  employeeAdditionalCostShareValue: Number(shareValue) || 0,
  rules: rules.map((rule) => ({
    weekDays: rule.weekDays.map(toApiWeekDay),
    allDay: Boolean(rule.allDay),
    periods: rule.allDay ? [] : rule.periods,
    adjustmentType: rule.adjustmentType,
    adjustmentValue: Number(rule.adjustmentValue) || 0,
  })),
});

const StoreTimePricingPanel = ({ storeId, store, canUpdate, hideProfessionalShare = false, onSaved }) => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const [form, setForm] = useState(() => fromApiRules(store?.timePricing));
  const [saving, setSaving] = useState(false);
  const currencySymbol = getCurrencySymbol(store?.currency || DEFAULT_CURRENCY);

  useEffect(() => {
    setForm(fromApiRules(store?.timePricing));
  }, [store?.timePricing]);

  const periodsByDay = useMemo(() => {
    const map = {};
    WEEK_DAYS.forEach((day) => {
      map[day] = [];
    });
    (store?.openingHours || []).forEach((dayHours) => {
      const dayKey = String(dayHours.weekDay || '').toLowerCase();
      if (map[dayKey]) {
        map[dayKey].push(...(dayHours.periods || []).map((period) => ({ ...period, source: 'opening' })));
      }
    });
    (store?.authorizationHours || []).forEach((dayHours) => {
      const dayKey = String(dayHours.weekDay || '').toLowerCase();
      if (map[dayKey]) {
        map[dayKey].push(...(dayHours.periods || []).map((period) => ({ ...period, source: 'authorization' })));
      }
    });
    return map;
  }, [store?.authorizationHours, store?.openingHours]);

  const updateRule = (ruleId, patch) => {
    setForm((current) => ({
      ...current,
      rules: current.rules.map((rule) => (rule.id === ruleId ? { ...rule, ...patch } : rule)),
    }));
  };

  const toggleWeekDay = (rule, day) => {
    const selected = rule.weekDays.includes(day)
      ? rule.weekDays.filter((item) => item !== day)
      : [...rule.weekDays, day];
    updateRule(rule.id, { weekDays: selected });
  };

  const applyPreset = (rule, period) => {
    updateRule(rule.id, {
      allDay: false,
      periods: [{ from: period.from, to: period.to }],
    });
  };

  const validate = () => {
    if (!hideProfessionalShare) {
      if (Number(form.shareValue) < 0) {
        showError(t('stores.timePricing.validation.shareNegative'));
        return false;
      }
      if (form.shareType === 'Percentage' && Number(form.shareValue) > 100) {
        showError(t('stores.timePricing.validation.sharePercent'));
        return false;
      }
    }

    return form.rules.every((rule) => {
      if (!rule.weekDays.length) {
        showError(t('stores.timePricing.validation.weekDays'));
        return false;
      }
      if (Number(rule.adjustmentValue) < 0) {
        showError(t('stores.timePricing.validation.amountNegative'));
        return false;
      }
      if (rule.adjustmentType === 'Percentage' && Number(rule.adjustmentValue) > 100) {
        showError(t('stores.timePricing.validation.percentMax'));
        return false;
      }
      if (!rule.allDay) {
        if (!rule.periods.length) {
          showError(t('stores.timePricing.validation.periodRequired'));
          return false;
        }
        for (const period of rule.periods) {
          if (period.from >= period.to) {
            showError(t('storeRegistration.validation.openTimeAfterCloseTime'));
            return false;
          }
        }
      }
      return true;
    });
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    try {
      setSaving(true);
      const saved = await StoreService.updateTimePricing(storeId, toApiPayload(form.shareType, form.shareValue, form.rules));
      showSuccess(t('stores.timePricing.saved'));
      onSaved?.(saved);
    } catch (error) {
      const code = error?.response?.data?.errorCode ?? error?.response?.data?.ErrorCode;
      if (code === 4029) {
        showError(t('stores.timePricing.validation.overlap'));
      } else {
        showError(error?.response?.data?.message || t('stores.error.updateTimePricing'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3, md: 4 }, minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('stores.timePricing.helper')}
      </Typography>

      {!hideProfessionalShare && (
      <Box
        sx={{
          border: `1px solid ${BRAND.line}`,
          borderRadius: 2,
          backgroundColor: BRAND.surface,
          p: 2,
          mb: 3,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          {t('stores.timePricing.shareTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('stores.timePricing.shareHelper')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <SalovareSelectField
            label={t('stores.timePricing.shareType')}
            value={form.shareType}
            disabled={!canUpdate}
            onChange={(event) => setForm((current) => ({ ...current, shareType: event.target.value }))}
            sx={{ maxWidth: 220 }}
          >
            {ADJUSTMENT_TYPES.map((type) => (
              <MenuItem key={type} value={type}>{t(`stores.timePricing.adjustmentTypes.${type}`)}</MenuItem>
            ))}
          </SalovareSelectField>
          <Box sx={{ minWidth: 160, flex: '0 0 160px' }}>
            <Typography variant="subtitle2" sx={fieldLabelSx}>
              {t('stores.timePricing.shareValue')}
            </Typography>
            <TextField
              type="number"
              value={form.shareValue}
              disabled={!canUpdate}
              onChange={(event) => setForm((current) => ({ ...current, shareValue: event.target.value }))}
              inputProps={{ min: 0, step: '0.01' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {unitForAdjustment(form.shareType, currencySymbol)}
                  </InputAdornment>
                ),
              }}
              sx={buildAdornmentTextFieldSx()}
              fullWidth
            />
          </Box>
        </Box>
      </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {t('stores.timePricing.rulesTitle')}
        </Typography>
        {canUpdate && (
          <Button
            startIcon={<AddIcon />}
            onClick={() => setForm((current) => ({ ...current, rules: [...current.rules, createEmptyRule()] }))}
            sx={{ color: BRAND.accentText, fontWeight: 600, textTransform: 'none' }}
          >
            {t('stores.timePricing.addRule')}
          </Button>
        )}
      </Box>

      {form.rules.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('stores.timePricing.empty')}
        </Typography>
      ) : form.rules.map((rule, index) => {
        const presetPeriods = [...new Map(
          rule.weekDays.flatMap((day) => periodsByDay[day] || []).map((period) => [`${period.from}-${period.to}`, period]),
        ).values()];

        return (
          <Box
            key={rule.id}
            sx={{
              border: `1px solid ${BRAND.line}`,
              borderRadius: 2,
              backgroundColor: BRAND.surface,
              p: 2,
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {t('stores.timePricing.ruleLabel', { index: index + 1 })}
              </Typography>
              {canUpdate && (
                <IconButton
                  size="small"
                  onClick={() => setForm((current) => ({
                    ...current,
                    rules: current.rules.filter((item) => item.id !== rule.id),
                  }))}
                  sx={{ color: '#c0392b' }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            <Typography variant="subtitle2" sx={fieldLabelSx}>
              {t('stores.timePricing.weekDays')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
              {WEEK_DAYS.map((day) => {
                const selected = rule.weekDays.includes(day);
                return (
                  <Chip
                    key={day}
                    label={t(`common.days.${day}`)}
                    clickable={canUpdate}
                    onClick={canUpdate ? () => toggleWeekDay(rule, day) : undefined}
                    sx={{
                      fontWeight: 600,
                      textTransform: 'capitalize',
                      backgroundColor: selected ? brandAlpha('accent', 0.16) : brandAlpha('text', 0.04),
                      color: selected ? BRAND.accentText : BRAND.textMuted,
                      border: selected ? `1px solid ${brandAlpha('accent', 0.35)}` : '1px solid transparent',
                    }}
                  />
                );
              })}
            </Box>

            <SwitchField
              checked={rule.allDay}
              disabled={!canUpdate}
              onChange={(event) => updateRule(rule.id, { allDay: event.target.checked })}
              label={t('stores.timePricing.allDay')}
              helpText={rule.allDay ? t('stores.timePricing.allDayHelper') : undefined}
              containerSx={{ mb: 2 }}
            />

            {!rule.allDay && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                {rule.periods.map((period, periodIndex) => {
                  const otherPeriods = rule.periods.filter((_, index) => index !== periodIndex);
                  const startOptions = getExtraHoursStartOptions(TIME_OPTIONS, period, [], otherPeriods);
                  const endOptions = getExtraHoursEndOptions(TIME_OPTIONS, period, [], otherPeriods);
                  return (
                    <Box key={`${rule.id}-${periodIndex}`} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                      <SalovareSelectField
                        label={periodIndex === 0 ? t('stores.timePricing.from') : undefined}
                        value={period.from}
                        disabled={!canUpdate}
                        onChange={(event) => {
                          const periods = rule.periods.map((item, index) => (
                            index === periodIndex ? { ...item, from: event.target.value } : item
                          ));
                          updateRule(rule.id, { periods });
                        }}
                        sx={{ width: 140 }}
                      >
                        {startOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </SalovareSelectField>
                      <SalovareSelectField
                        label={periodIndex === 0 ? t('stores.timePricing.to') : undefined}
                        value={period.to}
                        disabled={!canUpdate}
                        onChange={(event) => {
                          const periods = rule.periods.map((item, index) => (
                            index === periodIndex ? { ...item, to: event.target.value } : item
                          ));
                          updateRule(rule.id, { periods });
                        }}
                        sx={{ width: 140 }}
                      >
                        {endOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </SalovareSelectField>
                      {canUpdate && rule.periods.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => updateRule(rule.id, {
                            periods: rule.periods.filter((_, index) => index !== periodIndex),
                          })}
                          sx={{ color: '#c0392b', mt: periodIndex === 0 ? 3 : 0 }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  );
                })}
                {canUpdate && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const last = rule.periods[rule.periods.length - 1];
                      updateRule(rule.id, {
                        periods: [...rule.periods, { from: last?.to || '19:00', to: '21:00' }],
                      });
                    }}
                    sx={{ alignSelf: 'flex-start', color: BRAND.accentText, fontWeight: 600, textTransform: 'none' }}
                  >
                    {t('storeRegistration.businessHours.addPeriod')}
                  </Button>
                )}
                {presetPeriods.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ color: BRAND.textMuted, display: 'block', mb: 0.75 }}>
                      {t('stores.timePricing.presets')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {presetPeriods.map((period) => (
                        <Chip
                          key={`${period.from}-${period.to}-${period.source}`}
                          label={`${period.from} – ${period.to}`}
                          size="small"
                          clickable={canUpdate}
                          onClick={canUpdate ? () => applyPreset(rule, period) : undefined}
                          sx={{
                            fontSize: '0.7rem',
                            height: 22,
                            backgroundColor: brandAlpha('accent', 0.10),
                            color: BRAND.accentText,
                            border: `1px solid ${brandAlpha('accent', 0.25)}`,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <SalovareSelectField
                label={t('stores.timePricing.adjustmentType')}
                value={rule.adjustmentType}
                disabled={!canUpdate}
                onChange={(event) => updateRule(rule.id, { adjustmentType: event.target.value })}
                sx={{ maxWidth: 220 }}
              >
                {ADJUSTMENT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>{t(`stores.timePricing.adjustmentTypes.${type}`)}</MenuItem>
                ))}
              </SalovareSelectField>
              <Box sx={{ minWidth: 160, flex: '0 0 160px' }}>
                <Typography variant="subtitle2" sx={fieldLabelSx}>
                  {t('stores.timePricing.adjustmentValue')}
                </Typography>
                <TextField
                  type="number"
                  value={rule.adjustmentValue}
                  disabled={!canUpdate}
                  onChange={(event) => updateRule(rule.id, { adjustmentValue: event.target.value })}
                  inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {unitForAdjustment(rule.adjustmentType, currencySymbol)}
                      </InputAdornment>
                    ),
                  }}
                  sx={buildAdornmentTextFieldSx()}
                  fullWidth
                />
              </Box>
            </Box>
          </Box>
        );
      })}

      {canUpdate && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Button
            color="inherit"
            onClick={() => setForm(fromApiRules(store?.timePricing))}
            disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton onClick={handleSave} disabled={saving}>
            {saving ? t('common.saving') : t('stores.timePricing.save')}
          </PrimaryActionButton>
        </Box>
      )}
    </Box>
  );
};

export default StoreTimePricingPanel;
